import { createReducer } from './utils';
import { clearAbort, createAbort, ABORT_KEYS } from './utils/abortManager';
import {
  AISLES_BY_TASKS_SUCCESS,
  AISLES_FAILURE,
  AISLES_LAYOUT_SUCCESS,
  AISLES_LIST_SEARCH,
  AISLES_LOADING,
  AISLES_SUCCESS,
} from './constants';

const defaultState = {
  loading: false,
  aisles: [],
  aislesFilter: [],
  aislesByTasks: [],
  aislesPendingTasks: {},
  aisleClientNames: {},
  layout: {},
  errorMessage: null,
};

// reducer

export const reducer = createReducer(defaultState, {
  [AISLES_LOADING]: handleAislesLoading,
  [AISLES_SUCCESS]: handleAislesSuccess,
  [AISLES_BY_TASKS_SUCCESS]: handleAislesByTasksSuccess,
  [AISLES_FAILURE]: handleAislesFailure,
  [AISLES_LIST_SEARCH]: handleAislesListSearch,
  [AISLES_LAYOUT_SUCCESS]: handleAislesLayoutSuccess,
});

function handleAislesLoading(state, action) {
  return {
    ...state,
    loading: true,
  };
}

function handleAislesSuccess(state, { payload: { aisleList } }) {
  return {
    ...state,
    loading: false,
    aisles: aisleList,
    aislesFilter: aisleList,
  };
}

function handleAislesByTasksSuccess(
  state,
  { payload: { aisleList, aislesPendingTasks, aisleClientNames } },
) {
  return {
    ...state,
    loading: false,
    aislesByTasks: aisleList,
    aislesPendingTasks: aislesPendingTasks,
    aisleClientNames: aisleClientNames,
  };
}

function handleAislesFailure(state, { payload: { error } }) {
  return {
    ...state,
    loading: false,
    images: [],
    errorMessage: error,
  };
}

function handleAislesListSearch(state, { payload: { aisleList } }) {
  return {
    ...state,
    loading: false,
    aislesFilter: aisleList,
  };
}

function handleAislesLayoutSuccess(state, { payload: { aisleLayout } }) {
  return {
    ...state,
    loading: false,
    layout: aisleLayout,
  };
}

// actions

export function getAisles() {
  return async (dispatch, getState, { services: { dataSource } }) => {
    dispatch({ type: AISLES_LOADING });

    const {
      stores: { currentStore },
    } = getState();

    try {
      let parameters = {};
      parameters['store'] = currentStore;

      const response = await dataSource.requestResourcesAisles({
        parameters: parameters,
      });

      const aisleList =
        Object.keys(response).length === 0 && response.constructor === Object
          ? []
          : response.map(element => element.aisle);

      dispatch({ type: AISLES_SUCCESS, payload: { aisleList } });
    } catch (error) {
      console.log('getAisles error', error);
      dispatch({
        type: AISLES_FAILURE,
        payload: { error },
      });
    }
  };
}

export function setAislesFilter(aisleList) {
  return async (dispatch, getState, { services: { } }) => {
    try {
      dispatch({
        type: AISLES_LIST_SEARCH,
        payload: { aisleList },
      });
    } catch (error) {
      console.log(error);
    }
  };
}

export function getAislesByTasks() {
  return async (dispatch, getState, { services: { dataSource } }) => {
    dispatch({ type: AISLES_LOADING });

    const {
      stores: { currentStore },
      filters: { filters },
      tasks: { currentTaskType },
    } = getState();

    try {
      let parameters = {};
      parameters['store'] = currentStore;
      parameters['priority'] = filters['priority'];
      parameters['task_type'] = currentTaskType || filters['task_type'];
      parameters['completed'] = filters['completed'];
      parameters['supplier_id'] = filters['supplier_id'];

      // stockout filter
      if (currentTaskType === 'stockout') {
        parameters['spread_to_fill'] = filters['spread_to_fill'];
      }

      // Remove null/undefined/empty-string params
      Object.keys(parameters).forEach((key) => {
        if (parameters[key] === null || parameters[key] === undefined || parameters[key] === '') {
          delete parameters[key];
        }
      });

      let aislesPendingTasks = {};
      let aisleClientNames = {};
      let aisleList = [];

      try {
        const signal = createAbort(ABORT_KEYS.TASK_AISLES);
        const response = await dataSource.requestResourcesAisles({
          parameters: parameters,
          signal,
        });
        clearAbort(ABORT_KEYS.TASK_AISLES);
        response.map(data => {
          // api-tasks resolves this from the store layout and sends null when
          // the client never named the aisle. Keeping only the names it did
          // resolve is what lets the chips skip the layout entirely.
          if (data.client_aisle_name) {
            aisleClientNames[data.aisle] = data.client_aisle_name;
          }

          if (!filters['completed']) {
            aislesPendingTasks[data.aisle] = data.pending_tasks;
          } else {
            aislesPendingTasks[data.aisle] =
              data.total_tasks > -1 && data.pending_tasks > -1
                ? Math.abs(data.total_tasks - data.pending_tasks)
                : -1;
          }
        });

        aisleList = response.map(element => element.aisle);
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        aislesPendingTasks = {};
        aisleClientNames = {};
        aisleList = [];
      }

      dispatch({
        type: AISLES_BY_TASKS_SUCCESS,
        payload: { aisleList, aislesPendingTasks, aisleClientNames },
      });
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.log('getAislesByTasks error', error);
      dispatch({
        type: AISLES_FAILURE,
        payload: { error },
      });
    }
  };
}

export function getLayout() {
  return async (dispatch, getState, { services: { dataSource } }) => {
    dispatch({ type: AISLES_LOADING });

    const {
      stores: { currentStore },
    } = getState();

    try {
      const response = await dataSource.requestAislesLayout({
        store: currentStore,
      });

      if (response['message'] !== 'Unauthorized') {
        const aisleLayout =
          Object.keys(response).length === 0 && response.constructor === Object
            ? []
            : response?.data?.layout?.capture;
        dispatch({ type: AISLES_LAYOUT_SUCCESS, payload: { aisleLayout } });
      }
    } catch (error) {
      console.log('getLayout error', error);
      dispatch({
        type: AISLES_FAILURE,
        payload: { error },
      });
    }
  };
}
